import { GetStagesItem } from "@microtica/ms-engine-sdk";
import { Button, Tooltip, Typography } from "antd";
import { Settings, Clock, Link as LinkIcon } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import moment from "moment-timezone";
import CloudIcon from "../CloudIcon";
import IaCIcon from "../IaCIcon";
import DeploymentStatusTag from "../DeploymentStatusTag";
import { AWS_REGIONS, GCP_REGIONS, EXOSCALE_ZONES } from "../../enums/enums";
import { CloudAccountMap, IaC } from "../../types";

const { Paragraph, Text } = Typography;

interface EnvironmentCardProps {
    environment: GetStagesItem;
    cloudAccountNames?: CloudAccountMap;
}

const EnvironmentCard = ({ environment: env, cloudAccountNames = {} }: EnvironmentCardProps) => {
    const navigate = useNavigate();
    const { projectId } = useParams() as { projectId: string };

    const getRegionName = () => {
        const region = env.awsRegion || env.gcpRegion || env.exoscaleZone;
        if (!region) return null;

        if (env.cloudProvider === "aws") {
            return AWS_REGIONS.find(r => r.value === region || r.id === region)?.name;
        } else if (env.cloudProvider === "google") {
            return GCP_REGIONS.find(r => r.value === region || r.id === region)?.name;
        } else if (env.cloudProvider === "exoscale") {
            return EXOSCALE_ZONES.find(z => z.value === region || z.id === region)?.name;
        }
        return region;
    };

    const getAccountId = () => {
        return env.awsAccountId || env.gcpProjectId || env.exoscaleAccountId;
    };

    const getAccountName = () => {
        const accountId = getAccountId();
        if (!accountId) return null;
        return cloudAccountNames[accountId] || accountId;
    };

    const getIaCLabel = () => {
        switch (env.infrastructureAsCodeTool) {
            case "cloudformation": return "CloudFormation";
            case "terraform": return "Terraform";
            default: return env.infrastructureAsCodeTool;
        }
    };

    const getCloudLabel = () => {
        switch (env.cloudProvider) {
            case "aws": return "AWS";
            case "google": return "GCP";
            case "exoscale": return "Exoscale";
            case "azurerm": return "Azure";
            default: return env.cloudProvider;
        }
    };

    const regionName = getRegionName();
    const accountId = getAccountId();
    const accountName = getAccountName();
    const hasCloudAccount = !!accountId;

    return (
        <div
            className="environment-card"
            onClick={() => navigate(`/projects/${projectId}/environments/${env.id}`)}
        >
            {/* Header: Name and Status */}
            <div className="environment-card-header">
                <Text strong className="environment-card-name">
                    {env.name}
                </Text>
                <DeploymentStatusTag status={env.status} size="large" />
            </div>

            {/* Description */}
            <Paragraph
                ellipsis={{ rows: 2, tooltip: env.description }}
                className="environment-card-description"
            >
                {env.description || <span className="environment-card-no-desc">No description</span>}
            </Paragraph>

            {/* Provider & IaC Info - Subtle inline display */}
            <div className="environment-card-meta">
                {env.cloudProvider && (
                    <div className="environment-meta-item">
                        <CloudIcon provider={env.cloudProvider} />
                        <span>{getCloudLabel()}</span>
                    </div>
                )}
                {env.cloudProvider && env.infrastructureAsCodeTool && (
                    <span className="environment-meta-separator">·</span>
                )}
                {env.infrastructureAsCodeTool && (
                    <div className="environment-meta-item">
                        <IaCIcon provider={env.infrastructureAsCodeTool as IaC} />
                        <span>{getIaCLabel()}</span>
                    </div>
                )}
            </div>

            {/* Account & Region info OR Link cloud account */}
            {hasCloudAccount ? (
                <div className="environment-card-region">
                    {accountName}
                    {regionName && (
                        <>
                            <span className="environment-meta-separator">·</span>
                            {regionName}
                        </>
                    )}
                </div>
            ) : (
                <div className="environment-card-link-account">
                    <Link
                        to={`/projects/${projectId}/environments/${env.id}/settings`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <LinkIcon size={14} /> Link cloud account
                    </Link>
                </div>
            )}

            {/* Spacer to push footer to bottom */}
            <div className="environment-card-spacer" />

            {/* Footer */}
            <div className="environment-card-footer">
                <div className="environment-card-updated">
                    <Clock size={14} />
                    <span>
                        {env.lastDeployed
                            ? moment(env.lastDeployed).fromNow()
                            : "Never deployed"}
                    </span>
                </div>
                <Link
                    to={`/projects/${projectId}/environments/${env.id}/settings`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <Button
                        type="text"
                        icon={<Settings size={16} />}
                        className="environment-card-settings-btn"
                    />
                </Link>
            </div>
        </div>
    );
};

export default EnvironmentCard;
